import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Input, Checkbox, Button, Empty, Spin, Card, Image, Row, Col } from 'antd';
import { Dialog } from '@base-ui/react/dialog';
import { MagnifyingGlassIcon, XIcon } from '@phosphor-icons/react';
import { updateAttributesOfCdnImage } from '@admin-panel/utils';
import {
  useLazyTypeAheadApiQuery,
  useGetRecommendationsMutation,
} from '../../../../store/stylesSearch/stylesSearchApi';
import { useUpdateLookContentMutation } from '../../../../store/stylist/stylistApi';
import type { RecommendedProductDTOType } from '../../../../store/stylesSearch/schemas';
import {
  getProductDepth,
  ProductDepthRail,
} from '../../../../components/LookContentEditor/productDepth';
import type { AddProductsModalProps } from './types';
import {
  getInitialSelectedImages,
  getInitialSelection,
  getProductStyleNumbers,
  hasSelectionChanged,
} from './helpers';

export const AddProductsModal = ({
  isOpen,
  onClose,
  selectedProductType,
  portalContainer,
  lookId,
  currentMappedIds = [],
  currentMappedImages = {},
  onSuccess,
  currentRecommendations = [],
  mode = 'recommendations',
  currentSelectedIds = [],
  onSelectionSave,
}: AddProductsModalProps) => {
  const initialSelection = getInitialSelection(mode, currentSelectedIds, currentMappedIds);
  const initialSelectedImages = getInitialSelectedImages(currentMappedImages);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProducts, setSelectedProducts] = useState<Array<string>>(initialSelection);
  const [selectedProductImages, setSelectedProductImages] =
    useState<Record<string, string>>(initialSelectedImages);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [initialSelectedProducts] = useState<Array<string>>(initialSelection);
  const [isSavingSelection, setIsSavingSelection] = useState(false);

  // Refs for debouncing
  const typeaheadTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [triggerTypeahead, { data: typeaheadData, isLoading: isTypeaheadLoading }] =
    useLazyTypeAheadApiQuery();
  const [triggerRecommendations, { data: recommendationsData, isLoading: isSearching }] =
    useGetRecommendationsMutation();
  const [updateLookContent, { isLoading: isUpdating }] = useUpdateLookContentMutation();

  const typeaheadOptions = useMemo(
    () =>
      (typeaheadData?.products || []).map(product => ({
        value: product.title,
        label: `${product.title} - ${product.brand} - ₹${product.price}`,
        product,
      })),
    [typeaheadData?.products],
  );
  const searchResults = useMemo(() => recommendationsData?.products || [], [recommendationsData]);
  const showDropdown =
    isSearchFocused && searchQuery.trim().length > 2 && typeaheadOptions.length > 0;

  const triggerTypeaheadRef = useRef(triggerTypeahead);
  useEffect(() => {
    triggerTypeaheadRef.current = triggerTypeahead;
  }, [triggerTypeahead]);

  // Debounced typeahead search with improved cleanup
  useEffect(() => {
    // Clear any existing timeout
    if (typeaheadTimeoutRef.current) {
      clearTimeout(typeaheadTimeoutRef.current);
    }

    if (searchQuery.trim().length > 2) {
      typeaheadTimeoutRef.current = setTimeout(() => {
        triggerTypeaheadRef.current({ q: searchQuery });
      }, 300);
    }

    // Cleanup function
    return () => {
      if (typeaheadTimeoutRef.current) {
        clearTimeout(typeaheadTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const handleSearch = (searchValue: string) => {
    setSearchQuery(searchValue);
  };

  const handleEnterSearch = () => {
    if (searchQuery.trim().length > 0) {
      setIsSearchFocused(false);
      // Trigger the recommendations search with the typed query
      triggerRecommendations({
        searchType: 'SEMANTIC',
        text: searchQuery.trim(),
        selectedFacets: {
          'Shopify Status': ['ACTIVE'],
          'In-Stock': ['true'],
        },
      });
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleEnterSearch();
    }
  };

  const handleSelect = (product: RecommendedProductDTOType) => {
    setSearchQuery(product.title);
    setIsSearchFocused(false);

    triggerRecommendations({
      searchType: 'FREETEXT',
      // searchType: 'PRODUCT',
      text: product.title,
      selectedFacets: {
        'Shopify Status': ['ACTIVE'],
        'In-Stock': ['true'],
      },
    });
  };

  const handleStyleNumberSelect = (styleNumber: string, imageUrl: string, checked: boolean) => {
    if (!styleNumber) {
      return;
    }

    if (checked) {
      setSelectedProducts(prev => Array.from(new Set([...prev, styleNumber])));
      setSelectedProductImages(prev => ({
        ...prev,
        [styleNumber]: imageUrl,
      }));
    } else {
      setSelectedProducts(prev => prev.filter(id => id !== styleNumber));
      setSelectedProductImages(prev => {
        const newImages = { ...prev };
        delete newImages[styleNumber];
        return newImages;
      });
    }
  };

  const isSelectionChanged = hasSelectionChanged(selectedProducts, initialSelectedProducts);
  const isDoneDisabled = isSavingSelection || (mode === 'selection' ? false : !isSelectionChanged);

  const handleDone = async () => {
    if (mode === 'selection') {
      const uniqueSelection = Array.from(new Set(selectedProducts));
      try {
        setIsSavingSelection(true);
        await onSelectionSave?.(uniqueSelection, selectedProductImages);
        onClose();
      } finally {
        setIsSavingSelection(false);
      }
      return;
    }

    if (lookId === undefined || lookId === null) {
      return;
    }

    try {
      // Update the current recommendations to include mapped LK styles and images
      const mappedIdsAsStrings = Array.from(new Set(selectedProducts.map(value => String(value))));
      const updatedRecommendations = currentRecommendations.map(rec => {
        if (rec.product_type === selectedProductType) {
          return {
            ...rec,
            mapped_lk_styles: mappedIdsAsStrings,
            mapped_product_images: selectedProductImages,
          };
        }
        return rec;
      });

      // Prepare the data for the API call (convert to the format expected by the backend)
      const apiRecommendations = updatedRecommendations.map(rec => ({
        product_type: rec.product_type,
        description: rec.description,
        mapped_lk_styles: rec.mapped_lk_styles?.map(id => id.toString()) || [],
        mapped_product_images: rec.mapped_product_images || {},
      }));

      await updateLookContent({
        id: Number(lookId),
        data: {
          product_recommendations: apiRecommendations,
        },
      }).unwrap();

      onSuccess?.(updatedRecommendations);
      onClose();
    } catch (error) {
      console.error('Error updating mapped products:', error);
    }
  };

  const handleModalClose = () => {
    onClose();
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={open => {
        if (!open) {
          onClose();
        }
      }}
    >
      <Dialog.Portal container={portalContainer ?? undefined}>
        <Dialog.Backdrop className="fixed inset-0 z-[1500] bg-black/35 backdrop-blur-sm" />
        <Dialog.Popup className="fixed inset-0 z-[1501] grid place-items-center p-4 focus-visible:outline-none">
          <div className="flex h-[80vh] max-h-175 w-[min(96vw,800px)] flex-col overflow-hidden rounded-xl bg-background shadow-2xl ring-1 ring-border/70">
            <header className="flex items-center justify-between border-b border-border px-4 py-3">
              <Dialog.Title className="m-0 text-base font-semibold text-foreground">
                {`Add Products - ${selectedProductType.replace('_', ' ').toUpperCase()}`}
              </Dialog.Title>
              <button
                type="button"
                onClick={handleModalClose}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground"
                aria-label="Close"
              >
                <XIcon size={16} />
              </button>
            </header>

            <div
              style={{
                padding: '20px',
                height: 'calc(80vh - 140px)', // Subtract header and footer height
                maxHeight: '560px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div style={{ marginBottom: '20px', position: 'relative' }}>
                <Input
                  prefix={<MagnifyingGlassIcon />}
                  placeholder={`Search for ${selectedProductType.replace('_', ' ')} products...`}
                  value={searchQuery}
                  onChange={e => handleSearch(e.target.value)}
                  onKeyDown={handleKeyPress}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => setIsSearchFocused(false)}
                  style={{ width: '100%' }}
                />

                {/* Custom dropdown with images */}
                {showDropdown && typeaheadOptions.length > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #d9d9d9',
                      borderRadius: '6px',
                      boxShadow: '0 6px 16px 0 rgba(0, 0, 0, 0.08)',
                      zIndex: 1050,
                      maxHeight: '300px',
                      overflowY: 'auto',
                    }}
                    onClick={e => e.stopPropagation()}
                  >
                    {isTypeaheadLoading ? (
                      <div style={{ padding: '16px', textAlign: 'center' }}>
                        <Spin size="small" />
                      </div>
                    ) : (
                      typeaheadOptions.map((option, index) => {
                        const product = option.product;
                        if (!product) return null;

                        const optimizedImage = updateAttributesOfCdnImage(product.default_image, {
                          width: 60,
                          quality: 85,
                        });

                        return (
                          <div
                            key={index}
                            style={{
                              padding: '12px 16px',
                              borderBottom:
                                index < typeaheadOptions.length - 1 ? '1px solid #f0f0f0' : 'none',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              transition: 'background-color 0.2s',
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.backgroundColor = '#f5f5f5';
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                            onMouseDown={e => {
                              e.preventDefault(); // Prevent input blur
                              handleSelect(product);
                            }}
                          >
                            {/* Product Image */}
                            <div
                              style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '4px',
                                overflow: 'hidden',
                                border: '1px solid #e0e0e0',
                                flexShrink: 0,
                              }}
                            >
                              <Image
                                src={optimizedImage || product.default_image}
                                alt={product.title}
                                preview={false}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                }}
                                fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBmaWxsPSIjRjBGMEYwIi8+Cjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSIgZm9udC1zaXplPSIxMHB4IiBmaWxsPSIjOTk5Ij5ObyBJbWFnZTwvdGV4dD4KPHN2Zz4K"
                              />
                            </div>

                            {/* Product Info */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                style={{
                                  fontWeight: 500,
                                  fontSize: '14px',
                                  color: '#262626',
                                  marginBottom: '2px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {product.title}
                              </div>
                              <div
                                style={{
                                  fontSize: '12px',
                                  color: '#8c8c8c',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {product.brand} • ₹{product.price}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>

              {/* Scrollable content area */}
              <div
                style={{
                  flex: 1,
                  overflow: 'auto',
                  paddingRight: '4px', // Space for scrollbar
                  opacity: isUpdating || isSavingSelection ? 0.6 : 1,
                  transition: 'opacity 0.2s ease',
                }}
              >
                {isSearching && (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Spin size="large" />
                    <div style={{ marginTop: '8px' }}>Searching products...</div>
                  </div>
                )}

                {searchQuery &&
                  !isSearching &&
                  !isTypeaheadLoading &&
                  searchResults.length === 0 &&
                  typeaheadOptions.length === 0 && (
                    <Empty description="No products found" style={{ margin: '40px 0' }} />
                  )}

                {searchResults.length > 0 && (
                  <div>
                    <Row gutter={[16, 16]}>
                      {searchResults.map(product => {
                        const styleNumbers = getProductStyleNumbers(product);
                        const hasLkStyleNumbers = styleNumbers.length > 0;
                        const selectedStylesForProduct = styleNumbers.filter(styleNumber =>
                          selectedProducts.includes(styleNumber),
                        );
                        const optimizedImage = product.default_image
                          ? updateAttributesOfCdnImage(product.default_image, {
                              width: 200,
                              quality: 100,
                            })
                          : null;
                        const imageUrl = optimizedImage || product.default_image;
                        const productDepth = getProductDepth(product);

                        return (
                          <Col
                            key={`${product.product_id}-${product.id}`}
                            xs={24}
                            sm={12}
                            md={8}
                            lg={6}
                          >
                            <Card
                              hoverable
                              style={{
                                height: '100%',
                                position: 'relative',
                                border:
                                  selectedStylesForProduct.length > 0
                                    ? '2px solid #1890ff'
                                    : '1px solid #f0f0f0',
                                opacity: hasLkStyleNumbers ? 1 : 0.6,
                              }}
                              bodyStyle={{ padding: '12px' }}
                            >
                              {selectedStylesForProduct.length > 0 ? (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: '8px',
                                    left: '8px',
                                    zIndex: 1,
                                    borderRadius: '9999px',
                                    backgroundColor: '#e6f4ff',
                                    border: '1px solid #91caff',
                                    padding: '2px 8px',
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    color: '#0958d9',
                                  }}
                                >
                                  {selectedStylesForProduct.length} selected
                                </div>
                              ) : null}

                              {/* Product Image */}
                              <div
                                style={{
                                  width: '100%',
                                  height: '160px',
                                  marginBottom: '8px',
                                  overflow: 'hidden',
                                  borderRadius: '4px',
                                  position: 'relative',
                                }}
                              >
                                <Image
                                  src={imageUrl}
                                  alt={product.title}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                  }}
                                  fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzY2NiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg=="
                                  preview={false}
                                />
                                <ProductDepthRail
                                  inventoryDepth={productDepth.inventoryDepth}
                                  jitDepth={productDepth.jitDepth}
                                />
                              </div>

                              {/* Product Info */}
                              <div>
                                <div
                                  style={{
                                    fontWeight: 600,
                                    fontSize: '14px',
                                    marginBottom: '4px',
                                    lineHeight: '1.3',
                                    height: '36px',
                                    overflow: 'hidden',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                  }}
                                  title={product.title}
                                >
                                  {product.title}
                                </div>

                                <div
                                  style={{
                                    color: '#666',
                                    fontSize: '12px',
                                    marginBottom: '4px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                  title={`${product.brand} • ${product.product_type}`}
                                >
                                  {product.brand} • {product.product_type}
                                </div>

                                <div
                                  style={{
                                    color: '#666',
                                    fontSize: '11px',
                                    marginBottom: '8px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                  title={`${product.color} • ${product.fabric}`}
                                >
                                  {product.color} • {product.fabric}
                                </div>

                                <div
                                  style={{
                                    display: 'flex',
                                    gap: '6px',
                                    alignItems: 'center',
                                  }}
                                >
                                  <span
                                    style={{
                                      fontWeight: 600,
                                      fontSize: '14px',
                                      color: '#1890ff',
                                    }}
                                  >
                                    ₹{product.price.toLocaleString()}
                                  </span>
                                  {product.mrp > product.price && (
                                    <span
                                      style={{
                                        textDecoration: 'line-through',
                                        color: '#999',
                                        fontSize: '12px',
                                      }}
                                    >
                                      ₹{product.mrp.toLocaleString()}
                                    </span>
                                  )}
                                </div>
                                <div style={{ marginTop: '10px' }}>
                                  {hasLkStyleNumbers ? (
                                    <div
                                      style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '6px',
                                      }}
                                    >
                                      {styleNumbers.map(styleNumber => {
                                        const isSelected = selectedProducts.includes(styleNumber);

                                        return (
                                          <label
                                            key={`${product.product_id}-${styleNumber}`}
                                            style={{
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              gap: '6px',
                                              padding: '4px 8px',
                                              borderRadius: '9999px',
                                              border: isSelected
                                                ? '1px solid #91caff'
                                                : '1px solid #d9d9d9',
                                              backgroundColor: isSelected ? '#e6f4ff' : '#fff',
                                              cursor: 'pointer',
                                            }}
                                          >
                                            <Checkbox
                                              checked={isSelected}
                                              onChange={event => {
                                                event.stopPropagation();
                                                handleStyleNumberSelect(
                                                  styleNumber,
                                                  imageUrl,
                                                  event.target.checked,
                                                );
                                              }}
                                            />
                                            <span
                                              style={{
                                                fontSize: '11px',
                                                fontWeight: 600,
                                                color: isSelected ? '#0958d9' : '#595959',
                                                wordBreak: 'break-all',
                                              }}
                                            >
                                              {styleNumber}
                                            </span>
                                          </label>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <div
                                      style={{
                                        marginTop: '6px',
                                        fontSize: '11px',
                                        color: '#b45309',
                                      }}
                                    >
                                      No LK style number found on product
                                    </div>
                                  )}
                                </div>
                              </div>
                            </Card>
                          </Col>
                        );
                      })}
                    </Row>
                  </div>
                )}

                {searchQuery.trim().length === 0 && (
                  <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
                    <MagnifyingGlassIcon style={{ fontSize: '48px', marginBottom: '16px' }} />
                    <div style={{ fontSize: '16px', marginBottom: '8px' }}>
                      Search for {selectedProductType.replace('_', ' ')} products
                    </div>
                    <div style={{ fontSize: '14px' }}>
                      Type at least 3 characters to start searching
                    </div>
                  </div>
                )}

                {searchQuery.trim().length > 0 && searchQuery.trim().length <= 2 && (
                  <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                    Type at least 3 characters to search
                  </div>
                )}
              </div>
            </div>

            {isSavingSelection ? (
              <div className="border-t border-border/60 bg-muted/30 px-4 py-2 text-sm text-muted-foreground">
                Saving selected products...
              </div>
            ) : null}

            <footer className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
              <Button onClick={handleModalClose} disabled={isSavingSelection}>
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={handleDone}
                loading={isUpdating || isSavingSelection}
                disabled={isDoneDisabled}
              >
                Done
              </Button>
            </footer>
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
